import { useState, useCallback, type RefObject } from "react";
import { timeToMinutes, minutesToTime, type DragState, type MoveDragState } from "./pricingGridUtils";

export function usePricingDrag(
  gridRef: RefObject<HTMLDivElement>,
  rules: any[],
  startMinutes: number,
  endMinutes: number,
  cellMinutes: number,
  onResizeRule?: (ruleId: string, newStart: string, newEnd: string) => void,
  onMoveRule?: (ruleId: string, data: { day_of_week: number[]; start_time: string; end_time: string }) => void,
) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [resizePreview, setResizePreview] = useState<{ ruleId: string; start: string; end: string } | null>(null);
  const [moveDrag, setMoveDrag] = useState<MoveDragState | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);

  const checkOverlap = useCallback((day: number, startMin: number, endMin: number, excludeIds: string[]) => {
    for (const rule of rules) {
      if (excludeIds.includes(rule.id)) continue;
      if (rule.day_of_week && rule.day_of_week.length > 0 && !rule.day_of_week.includes(day)) continue;
      const rStart = timeToMinutes(rule.start_time);
      const rEnd = timeToMinutes(rule.end_time);
      if (startMin < rEnd && endMin > rStart) {
        return rule.name || "annan regel";
      }
    }
    return null;
  }, [rules]);

  const getSelection = useCallback(() => {
    if (!drag) return null;
    const minDay = Math.min(drag.startDay, drag.endDay);
    const maxDay = Math.max(drag.startDay, drag.endDay);
    const minSlot = Math.min(drag.startSlot, drag.endSlot);
    const maxSlot = Math.max(drag.startSlot, drag.endSlot);
    return { minDay, maxDay, minSlot, maxSlot };
  }, [drag]);

  const isSelected = useCallback((day: number, slot: number) => {
    const sel = getSelection();
    if (!sel) return false;
    return day >= sel.minDay && day <= sel.maxDay && slot >= sel.minSlot && slot <= sel.maxSlot;
  }, [getSelection]);

  const handleDragStart = (day: number, slot: number, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-rule-block]")) return;
    e.preventDefault();
    setIsDragging(true);
    setDrag({ startDay: day, startSlot: slot, endDay: day, endSlot: slot });
  };

  const handleDragEnter = (day: number, slot: number) => {
    if (!isDragging || !drag) return;
    setDrag((prev) => prev ? { ...prev, endDay: day, endSlot: slot } : null);
  };

  const handleDragEnd = () => {
    if (!isDragging || !drag) {
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    return getSelection();
  };

  const handleResizeDragStart = (e: React.MouseEvent, rule: any, edge: "left" | "right", dayIndex: number) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onResizeRule || !gridRef.current) return;

    const ruleStart = timeToMinutes(rule.start_time);
    const ruleEnd = timeToMinutes(rule.end_time);

    const rowEl = gridRef.current.querySelectorAll("[data-day-row]")[dayIndex] as HTMLElement;
    if (!rowEl) return;

    const pending = { start: rule.start_time.slice(0, 5), end: rule.end_time.slice(0, 5) };

    const tooltip = document.createElement("div");
    tooltip.className = "fixed z-[100] bg-popover text-popover-foreground border rounded px-2 py-1 text-xs shadow-md pointer-events-none";
    document.body.appendChild(tooltip);

    const onMove = (ev: MouseEvent) => {
      const cells = rowEl.querySelectorAll("[data-slot]");
      if (!cells.length) return;
      const firstRect = cells[0].getBoundingClientRect();
      const lastRect = cells[cells.length - 1].getBoundingClientRect();
      const totalWidth = lastRect.right - firstRect.left;
      const xPct = Math.max(0, Math.min(1, (ev.clientX - firstRect.left) / totalWidth));
      const minute = Math.round((startMinutes + xPct * (endMinutes - startMinutes)) / cellMinutes) * cellMinutes;

      tooltip.style.left = `${ev.clientX + 12}px`;
      tooltip.style.top = `${ev.clientY - 28}px`;

      if (edge === "left" && minute < ruleEnd - cellMinutes) {
        const overlap = checkOverlap(dayIndex + 1, minute, ruleEnd, [rule.id]);
        tooltip.textContent = `${minutesToTime(minute)}–${minutesToTime(ruleEnd)}`;
        if (overlap) {
          tooltip.textContent += ` ⚠️ Överlappning`;
          tooltip.style.color = "hsl(var(--destructive))";
        } else {
          tooltip.style.color = "";
        }
        pending.start = minutesToTime(minute);
        pending.end = rule.end_time.slice(0, 5);
        setResizePreview({ ruleId: rule.id, start: pending.start, end: pending.end });
      } else if (edge === "right" && minute > ruleStart + cellMinutes) {
        const overlap = checkOverlap(dayIndex + 1, ruleStart, minute, [rule.id]);
        tooltip.textContent = `${minutesToTime(ruleStart)}–${minutesToTime(minute)}`;
        if (overlap) {
          tooltip.textContent += ` ⚠️ Överlappning`;
          tooltip.style.color = "hsl(var(--destructive))";
        } else {
          tooltip.style.color = "";
        }
        pending.start = rule.start_time.slice(0, 5);
        pending.end = minutesToTime(minute);
        setResizePreview({ ruleId: rule.id, start: pending.start, end: pending.end });
      }
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      tooltip.remove();
      onResizeRule(rule.id, pending.start, pending.end);
      setResizePreview(null);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleMoveDragStart = (e: React.MouseEvent, rule: any, day: number) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onMoveRule || !gridRef.current) return;

    const rStart = timeToMinutes(rule.start_time);
    const rEnd = timeToMinutes(rule.end_time);
    const duration = rEnd - rStart;

    setMoveDrag({
      ruleId: rule.id,
      rule,
      originDay: day,
      originStartMin: rStart,
      originEndMin: rEnd,
      durationMin: duration,
      currentDay: day,
      currentStartMin: rStart,
    });
    setIsMoving(true);

    const onMove = (ev: MouseEvent) => {
      if (!gridRef.current) return;
      const rows = gridRef.current.querySelectorAll("[data-day-row]");
      let closestDay = day;
      let minDist = Infinity;

      rows.forEach((row, idx) => {
        const rect = row.getBoundingClientRect();
        const dist = Math.abs(ev.clientY - (rect.top + rect.height / 2));
        if (dist < minDist) { minDist = dist; closestDay = idx + 1; }
      });

      const targetRow = rows[closestDay - 1] as HTMLElement;
      if (!targetRow) return;
      const cells = targetRow.querySelectorAll("[data-slot]");
      if (!cells.length) return;
      const firstRect = cells[0].getBoundingClientRect();
      const lastRect = cells[cells.length - 1].getBoundingClientRect();
      const totalWidth = lastRect.right - firstRect.left;
      const xPct = Math.max(0, Math.min(1, (ev.clientX - firstRect.left) / totalWidth));
      let snapMin = Math.round((startMinutes + xPct * (endMinutes - startMinutes)) / cellMinutes) * cellMinutes;
      snapMin = Math.max(startMinutes, Math.min(endMinutes - duration, snapMin));

      const overlap = checkOverlap(closestDay, snapMin, snapMin + duration, [rule.id]);
      setOverlapWarning(overlap);

      setMoveDrag((prev) => prev ? { ...prev, currentDay: closestDay, currentStartMin: snapMin } : null);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setIsMoving(false);

      setMoveDrag((prev) => {
        if (!prev) return null;
        const overlap = checkOverlap(prev.currentDay, prev.currentStartMin, prev.currentStartMin + prev.durationMin, [prev.ruleId]);
        if (!overlap) {
          onMoveRule(prev.ruleId, {
            day_of_week: [prev.currentDay],
            start_time: minutesToTime(prev.currentStartMin),
            end_time: minutesToTime(prev.currentStartMin + prev.durationMin),
          });
        }
        return null;
      });
      setOverlapWarning(null);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return {
    drag,
    setDrag,
    isDragging,
    setIsDragging,
    isSelected,
    getSelection,
    handleDragStart,
    handleDragEnter,
    handleDragEnd,
    resizePreview,
    handleResizeDragStart,
    moveDrag,
    isMoving,
    handleMoveDragStart,
    overlapWarning,
  };
}
